import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDeliveryPayload } from '../services/adminService';
import ActionPlan from '../components/ActionPlan';

// Step 10f Fix 4b: candidate-facing delivery page. Token = order UUID.
// Shows the polished output + copy buttons + paste instructions per section.
// Absolutely NO internal fields (scores, verdicts, QA notes, banned hits,
// AI metadata, costs, original profile, admin state).

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      alert('Press Ctrl+C / Cmd+C to copy.');
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
        copied ? 'bg-green-600 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'
      }`}
    >
      {copied ? '✓ Copied' : `📋 ${label}`}
    </button>
  );
};

const SectionCard = ({ title, instruction, children, copyText, copyLabel = 'Copy' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      {copyText && <CopyButton text={copyText} label={copyLabel} />}
    </div>
    {children}
    {instruction && (
      <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5 leading-relaxed">
        💡 {instruction}
      </div>
    )}
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap ${
      active
        ? 'border-purple-600 text-purple-700'
        : 'border-transparent text-slate-500 hover:text-slate-800'
    }`}
  >
    {children}
  </button>
);

const CandidateDelivery = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('profile');

  useEffect(() => {
    (async () => {
      try {
        const r = await getDeliveryPayload(token);
        setData(r);
      } catch (err) {
        setError(err.message || 'Delivery link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Where ActionPlan finds its lesson content, keyed by track. Set from an
  // effect rather than an injected <script>: React does not execute script
  // tags added via dangerouslySetInnerHTML, so that route would silently
  // leave window.__resources undefined and the component would fall back to
  // its hardcoded path.
  useEffect(() => {
    window.__resources = {
      ...(window.__resources || {}),
      planData: {
        entry_mid: '/data/entry_mid.json',
        senior_cxo: '/data/senior_cxo.json',
      },
    };
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-slate-500">Loading your profile…</div></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">🔗</div>
          <div className="text-xl font-semibold text-slate-900 mb-2">Link expired or invalid</div>
          <div className="text-slate-500 mb-4">{error}</div>
          <div className="text-xs text-slate-400">
            Reach out to <a href="mailto:contact@successlane.io" className="text-purple-700 hover:underline">contact@successlane.io</a> and we'll refresh your link.
          </div>
        </div>
      </div>
    );
  }

  const { profile, candidate, order_id, order_uuid, stage } = data;
  const roles = profile?.experience || [];
  const pinned = (profile?.pin_top_3 || []).filter(Boolean).slice(0, 3);
  const notes = profile?.notes_to_candidate || '';

  // The 100-day plan ships with the rewrite, so it appears for any delivered
  // order. `stage` is always 'delivered' here — the endpoint 404s otherwise —
  // but keying on it explicitly means the condition stays correct if that
  // endpoint's gate ever changes. order_uuid is the plan's auth token.
  const showPlan = stage === 'delivered' && !!order_uuid;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Branding header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-semibold">SL</div>
            <div className="text-sm font-semibold text-slate-900">Successlane</div>
          </div>
          {order_id && (
            <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{order_id}</span>
          )}
        </div>
      </div>

      {/* Tabs — only rendered when there is a second tab to switch to */}
      {showPlan && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-4 flex items-center gap-1 overflow-x-auto">
            <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>
              Your profile
            </TabButton>
            <TabButton active={tab === 'plan'} onClick={() => setTab('plan')}>
              100-day action plan
            </TabButton>
          </div>
        </div>
      )}

      {showPlan && tab === 'plan' && <ActionPlan orderId={order_uuid} userName={candidate?.name} />}

      <div className={`max-w-3xl mx-auto px-4 py-6${showPlan && tab !== 'profile' ? ' hidden' : ''}`}>
        {/* Greeting */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Hi {candidate?.first_name || 'there'}, your optimised LinkedIn profile is ready! 🎉
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            Copy each section below and paste it into your LinkedIn profile. We've included step-by-step guidance for each.
          </p>
        </div>

        {/* Notes from expert (if any) */}
        {notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Note from your expert</div>
            <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{notes}</div>
          </div>
        )}

        {/* Headline */}
        <SectionCard
          title="1. Headline"
          copyText={profile?.headline || ''}
          copyLabel="Copy headline"
          instruction="Go to your LinkedIn profile → click the pencil icon on your headline → paste this exactly."
        >
          <div className="text-sm text-slate-900 leading-relaxed p-3 bg-slate-50 rounded border-l-2 border-purple-400 whitespace-pre-wrap">
            {profile?.headline || <em className="text-slate-400">No headline generated</em>}
          </div>
        </SectionCard>

        {/* About */}
        <SectionCard
          title="2. About"
          copyText={profile?.about || ''}
          copyLabel="Copy About"
          instruction={`On your LinkedIn profile → click "Add profile section" → "About" (or edit the existing one) → replace with this text.`}
        >
          <div className="text-sm text-slate-900 leading-relaxed p-3 bg-slate-50 rounded border-l-2 border-purple-400 whitespace-pre-wrap max-h-[500px] overflow-y-auto">
            {profile?.about || <em className="text-slate-400">No About generated</em>}
          </div>
        </SectionCard>

        {/* Experience roles */}
        {roles.length > 0 && (
          <div className="mb-2 mt-6">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">3. Experience — {roles.length} role{roles.length === 1 ? '' : 's'}</div>
            {roles.map((role, idx) => {
              const title = role.title || role.job_title || '';
              const company = role.company || role.company_name || '';
              const dates = role.dates || (role.start_date ? `${role.start_date}${role.end_date || role.is_current ? ` – ${role.is_current ? 'Present' : role.end_date}` : ''}` : '');
              const descriptionText = [role.paragraph, ...(role.bullets || []).map(b => `• ${b}`)].filter(Boolean).join('\n\n');
              const skills = (role.skills || []).filter(Boolean);
              return (
                <div key={idx} className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
                      {company && <div className="text-xs text-slate-500">{company}{dates && ` · ${dates}`}</div>}
                    </div>
                    <CopyButton text={descriptionText} label="Copy description" />
                  </div>
                  {role.paragraph && (
                    <p className="text-sm text-slate-800 leading-relaxed mb-2 p-3 bg-slate-50 rounded border-l-2 border-purple-400">
                      {role.paragraph}
                    </p>
                  )}
                  {(role.bullets || []).length > 0 && (
                    <ul className="space-y-1 mb-2 p-3 bg-slate-50 rounded border-l-2 border-purple-400">
                      {role.bullets.map((b, bi) => (
                        <li key={bi} className="text-sm text-slate-800 flex gap-2">
                          <span className="text-purple-500 flex-shrink-0">•</span>{b}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5 leading-relaxed">
                    💡 Go to this role in your Experience section → click the pencil icon → paste the description above.
                  </div>
                  {skills.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <div className="text-[10px] font-semibold text-purple-700 uppercase tracking-wide mb-1.5">
                        Skills to attach to this role
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {skills.map((s, si) => (
                          <span key={si} className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs">
                            {s}
                          </span>
                        ))}
                        <CopyButton text={skills.join(', ')} label="Copy skills list" />
                      </div>
                      <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2.5 leading-relaxed">
                        💡 While editing this role, scroll down to the "Skills" section → click "Add skill" → add each of the skills above one by one. Attaching skills per-role gives you the highest LinkedIn search-ranking boost.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pin-top-3 */}
        {pinned.length > 0 && (
          <SectionCard
            title="4. Pin your Top 3 skills"
            copyText={pinned.join(', ')}
            copyLabel="Copy top 3"
            instruction={`Go to your profile's "Skills" section → click the pencil icon → drag these three to the top so they appear as your Top Skills. Pinned Top Skills get the highest search weight.`}
          >
            <div className="flex flex-wrap gap-1.5">
              {pinned.map((s, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-800 border border-purple-300 text-sm font-medium">
                  📌 {s}
                </span>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-slate-200 text-center text-xs text-slate-500 space-y-2">
          <div>
            Questions? Reply to your delivery email or contact us at{' '}
            <a href="mailto:contact@successlane.io" className="text-purple-700 hover:underline">contact@successlane.io</a>
          </div>
          <div>
            <a
              href={`https://successlane.io/feedback?order=${order_id || ''}`}
              className="inline-block px-3 py-1.5 rounded bg-amber-500 text-white font-semibold hover:bg-amber-600"
            >
              Share your feedback ★
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDelivery;
